import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import srOnly from '@helpers/srOnly';
import { paragraphSmall } from '@helpers/typography';
import ContentCardStyles from '@stories/Components/Cards/ContentCard/ContentCard.styles';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import { m } from 'framer-motion';
import styled, { createGlobalStyle, css } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --headerHeight: ${fluid(65, 99, Device.Mobile, Device.TabletLarge)} !important;

    @media ${from(Device.TabletLarge)} {
      --headerHeight: 147px !important;
    }
  }
`;
const Outerlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  width: 0;
  height: 0;
  background-color: transparent;
  opacity: 0.1;

  &[data-set-outerlay='true'] {
    height: 100%;
    width: 100%;
  }
`;
const Spacer = styled.div `
  height: 200vh;
`;
const Container = styled.header `
  --backgroundColor: var(--bgColor);
  --separatorColor: ${brand.global.separator};
  --separatorColorDark: var(--accentColorSecondary);
  --textColor: var(--primaryAltColor);

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9;
`;
const Inner = styled.div `
  ${siteWide};
  position: relative;
  z-index: 1;
`;
const TopArea = styled.div `
  ${transition('max-height')};
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  overflow: hidden;
  max-height: 100px;

  [data-show-reduced='true'] & {
    max-height: 0;
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const Grid = styled.div `
  ${baseGrid};
  min-height: 56px;
`;
const ContactArea = styled.div `
  grid-column: span 3;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;

  @media ${from(Device.Desktop)} {
    grid-column: span 6;
  }
`;
const TopLinks = styled.div `
  ${paragraphSmall};
  grid-column: 4 / span 5;

  padding: 10px;
  margin: 0;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 30px;

  @media ${from(Device.Desktop)} {
    grid-column: 7 / span 6;
    padding-right: 0;
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const LinkList = styled.ul ``;
const TopLink = styled.li `
  display: inline-block;
  margin-right: 48px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -24px;
    height: 100%;
    width: 1px;
    background-color: var(--separatorColor);
  }

  &:last-of-type {
    margin: 0;

    &::after {
      content: none;
    }
  }

  a {
    ${transition('text-decoration-color')};

    display: inline-block;
    color: var(--topLinkText);
    text-decoration: underline;
    text-decoration-color: transparent;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;

    &:focus {
      outline: none;
    }

    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-color: var(--topLinkText);
    }

    &:hover,
    &.focus-ring {
      text-decoration-color: currentColor;
    }
  }
`;
const LoginBtn = styled.a `
  ${transition('background-size, color', 0.4)};
  border: 1px solid var(--accentColorSecondary);
  padding: 6px 23px;
  background-color: transparent;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  border-radius: 4px;
  color: var(--accentColorSecondary);
  text-decoration: none;

  background-image: linear-gradient(-67.65deg, transparent 50%, var(--accentColorPrimary) 50%);
  background-repeat: no-repeat;
  background-size: 0% 100%;

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &:hover,
  &.focus-ring {
    background-size: 225% 100%;
    color: var(--bgColor);
    border-color: var(--accentColorPrimary);
  }
`;
const MainContainer = styled.div `
  ${transition('padding')};
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  padding: ${fluid(16, 24)} 0;
  position: relative;

  [data-show-reduced='true'] & {
    @media ${from(Device.TabletLarge)} {
      padding: 8px;
    }
  }

  > ${Inner} {
    ${baseGrid};

    @media ${until(Device.DesktopSmall)} {
      gap: 16px;
    }

    align-items: center;
  }
`;
const LogoWrapper = styled.a `
  display: block;
  grid-column: 1 / span 2;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 2;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 1 / span 3;

    [data-show-reduced='true'] & {
      grid-column: 1 / span 2;
    }
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }
`;
const Logo = styled.img `
  display: block;
  height: ${fluid(40, 44)};

  [data-show-reduced='true'] & {
    display: none;
  }

  &[data-logo-size='medium'] {
    height: ${fluid(45, 50)};
  }

  &[data-logo-size='large'] {
    height: ${fluid(50, 55)};
  }

  &[data-logo-size='xlarge'] {
    height: ${fluid(60, 70)};
  }
`;
const LogoReduced = styled.img `
  display: none;
  max-height: ${fluid(40, 44)};

  [data-show-reduced='true'] & {
    display: block;
  }
`;
const ActionWrapper = styled.div `
  grid-column: 3 / span 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${fluid(18, 36, Device.TabletLarge, Device.DesktopXL)};

  @media ${from(Device.Tablet)} {
    grid-column: 4 / span 5;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 3 / span 6;
    justify-content: space-between;
    gap: 10px;
  }

  @media ${from(Device.Desktop)} {
    gap: ${fluid(18, 36, Device.DesktopSmall, Device.DesktopXL)};
  }

  @media ${from(Device.Desktop)} {
    grid-column: 4 / span 9;
  }
`;
const HeaderContent = styled.div `
  display: flex;
  gap: 16px;

  > ${ButtonStyles.Container} {
    --buttonSize: 44px;
    order: 3;
    white-space: nowrap;
    min-width: 170px;

    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }
`;
const SearchButton = styled.button `
  ${ButtonReset};
  ${transition('background-color', 0.4)};

  order: 2;
  width: 44px;
  height: 44px;
  border-radius: 4px;
  display: grid;
  align-items: center;
  justify-content: center;

  > svg {
    ${transition('color', 0.4)};
    color: var(--textColor);
    display: block;
    height: 25px;
    width: 25px;
  }

  ${ActionWrapper}[data-action-open='true'] &,
  &:hover {
    @media ${from(Device.TabletLarge)} {
      background-color: var(--menuHoverColor);
    }
  }

  ${ActionWrapper}[data-action-open='true'] & {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }
`;
const SearchForm = styled.form `
  ${transition('transform')};
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  position: absolute;
  padding: 10px 12px 13px;
  width: 100%;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: -1;

  &[data-is-open='true'] {
    transform: translateY(100%);
  }
`;
const SearchInner = styled.div `
  ${siteWide};

  @media ${until(Device.MobileLarge)} {
    padding: 0;
  }
`;
const InputWrapper = styled.div `
  position: relative;
  width: 100%;
  margin: auto;

  @media ${from(Device.Mobile)} {
    max-width: ${fluid(350, 642)};
  }
`;
const SearchField = styled.input `
  height: 48px;
  border: 0;
  background-color: hsla(var(--fgColorString) / 0.1);
  border-radius: calc(var(--borderRadius) * 0.5);
  padding: 0 48px 0 16px;
  width: 100%;

  &:focus-visible {
    background-color: hsla(var(--fgColorString) / 0.15);
  }
`;
const SearchFormButton = styled.button `
  ${transition('opacity')};
  ${ButtonReset};

  padding: 12px 30px 14px;
  background-color: var(--separatorColorDark);
  border-radius: calc(var(--borderRadius) * 0.5);
  position: absolute;
  right: 0;
  top: 0;
  color: var(--bgColor);
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  text-transform: uppercase;

  > svg {
    color: var(--bgColor);
    display: block;
    height: 24px;
    width: 24px;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &:hover,
  &.focus-ring {
    opacity: 0.8;
  }
`;
const PhoneLink = styled.a `
  ${transition('opacity')};

  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--primaryColor);
  text-decoration: none;
  order: 1;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--primaryColor);
  }

  &:hover,
  &.focus-ring {
    opacity: 0.7;
  }

  > svg {
    display: block;
    height: 25px;
    width: 25px;
  }

  @media ${from(Device.TabletLarge)} {
    order: 2;
  }

  ${ActionWrapper}[data-action-open='true'] & {
    display: none;
  }
`;
const PhoneLinkMobile = styled(PhoneLink) `
  color: var(--primaryAltColor);

  svg {
    display: block;
    height: 25px;
    width: 25px;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const PhoneText = styled.span `
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;

  @media ${until(Device.TabletLarge)} {
    ${srOnly};
  }
`;
const PhoneNumber = styled.span `
  font-weight: 700;
  font-size: 16px;
  line-height: 28px;
  white-space: nowrap;

  > span {
    @media ${until(Device.TabletLarge)} {
      ${srOnly};
    }
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};

  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--textColor);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  order: 3;

  > svg {
    display: block;
    height: 16px;
    width: 16px;
  }

  ${ActionWrapper}[data-action-open='false'] & {
    display: none;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MenuButton = styled.button `
  ${ButtonReset};

  order: 3;

  > svg {
    color: var(--textColor);
    display: block;
    height: 25px;
    width: 25px;
  }

  ${ActionWrapper}[data-action-open='true'] & {
    display: none;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MobileNav = styled.nav `
  ${transition('transform')};

  position: fixed;
  height: calc(100dvh - var(--headerHeight));
  width: 100%;
  top: var(--headerHeight);
  background-color: var(--backgroundColor);
  overflow: auto;
  color: var(--textColor);

  &[data-is-open='false'] {
    transform: translateX(100%);
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MobileNavInner = styled.div `
  width: 100%;
`;
const PrimaryNavLinkStyles = css `
  font-weight: 700;
  display: block;
  font-size: 16px;
  line-height: 20px;
  color: currentColor;
  text-decoration: none;

  &:focus {
    outline: none;
  }
`;
const NavLinkStyles = css `
  ${transition('text-decoration-color')};

  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: currentColor;
  text-decoration: none;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: currentColor;
  }
`;
const MobileNavButton = styled.button `
  ${ButtonReset};
  ${PrimaryNavLinkStyles};

  padding: 14px var(--sitePadding);
  border-bottom: 1px solid var(--separatorColor);
  position: relative;
  width: 100%;
  text-align: left;

  &:not(a) {
    &::before,
    &::after {
      display: block;
      content: '';
      position: absolute;
      right: var(--sitePadding);
      top: 50%;
      width: 14px;
      height: 2px;
      background-color: var(--textColor);
    }

    &[data-is-open='true']::after,
    &::before {
      transform: translateY(-50%);
    }

    &::after {
      ${transition('transform')};

      transform: translateY(-50%) rotate(90deg);
    }
  }

  &[data-indent='true'] {
    padding-left: calc(var(--sitePadding) * 2);
    background-color: var(--highlightColorPrimary);

    a& {
      font-weight: 400;
    }
  }
`;
const MobileNavLink = styled.a `
  ${NavLinkStyles};

  display: block;
  padding: 14px var(--sitePadding) 14px calc(var(--sitePadding) * 3);
  border-bottom: 1px solid var(--separatorColor);
  width: 100%;
`;
const MobileColumnsWrapper = styled(m.div) `
  overflow: hidden;
`;
const DesktopNav = styled.nav `
  background: var(--backgroundColor);
  color: var(--fgColor);

  > ${Inner} {
    display: flex;
    justify-content: space-between;
    gap: ${fluid(16, 32)};

    @media ${from(Device.TabletLarge)} {
      gap: 16px;
      padding: 0;
    }

    @media ${from(Device.DesktopSmall)} {
      gap: ${fluid(16, 32)};
      padding: 0 var(--sitePadding);
    }
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const DesktopNavItem = styled(m.li) `
  position: relative;
`;
const DesktopPrimaryNavLink = styled.a `
  ${transition('border-color, padding')};
  ${PrimaryNavLinkStyles};
  display: inline-flex;
  align-items: center;
  gap: 10px;

  padding: 17px 0;
  border-bottom: 5px solid transparent;

  [data-show-reduced='true'] & {
    @media ${from(Device.TabletLarge)} {
      padding: 19px 0;
    }
  }

  svg {
    transform: rotate(90deg);
    height: 12px;
  }

  &:hover {
    cursor: pointer;
  }

  &.focus-ring,
  ${DesktopNavItem}[data-is-active='true'] & {
    border-color: var(--separatorColorDark);
    z-index: 2;
    position: relative;

    svg {
      transform: rotate(-90deg);
    }
  }
`;
const DropdownMenuColumn = styled(m.ul) `
  padding: 0;

  &[data-first-featured-column='true'] {
    margin-left: auto;
  }

  ${ContentCardStyles.Container} {
    margin: 0 0 14px;
  }

  ${ContentCardStyles.ContentWrapper} {
    padding: 18px 20px 11px;
  }

  ${ContentCardStyles.Title} {
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
  }
`;
const DropdownMenuColumnFirst = styled(DropdownMenuColumn) `
  ${transition('opacity')};

  position: absolute;
  top: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  background-color: var(--backgroundColor);
  border: 1px solid var(--separatorColor);
  border-top: none;
  width: 282px;
  z-index: 0;

  &:before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    height: 1px;
    width: 100%;
    border-top: 1px solid var(--separatorColor);
    z-index: 1;
  }

  [data-show-reduced='true'] & {
    top: calc(100% + 1px);
  }

  ${DesktopNavItem}[data-is-active='true'] & {
    max-height: calc(100vh - var(--headerHeight));
    opacity: 1;
    z-index: 1;

    &[data-is-simple='false'] {
      border-top: 0.5px solid var(--separatorColor);
    }
  }
`;
const DropdownMenuColumnThird = styled(DropdownMenuColumn) `
  display: none;
  min-height: calc(100% + 1px);

  &[data-is-active='true'] {
    position: absolute;
    top: 0;
    left: 100%;
    display: block;
    background-color: var(--backgroundColor);
    border: 1px solid var(--separatorColor);
    border-top: none;
    width: 282px;
    padding-top: 1px;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 1px;
      width: 100%;
      border-top: 1px solid var(--separatorColor);
      z-index: 1;
    }
  }
`;
const ExtraPadding = styled.div `
  padding-right: 20px;
`;
const DropdownMenuTitle = styled.li `
  ${PrimaryNavLinkStyles};

  padding-bottom: 8px;
  border-bottom: 2px solid var(--separatorColorDark);
  margin: 10px 0 26px;
`;
const DropdownMenuLink = styled.a `
  ${NavLinkStyles};
  ${transition('padding')};
  padding: 10px 19px 10px 28px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  text-decoration: none;
  position: relative;

  svg {
    transform: translateY(4px);
    height: 14px;
    flex: 0 0 8px;
  }
`;
const DropdownMenuItem = styled.li `
  ${transition('background-color')};

  position: relative;

  &:last-of-type {
    &::after {
      content: none;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 16px;
    height: 1px;
    width: calc(100% - 30px);
    border-bottom: 1px solid var(--separatorColor);
  }

  &:before {
    ${transition('opacity')};

    content: '';
    position: absolute;
    left: 22px;
    top: 50%;
    transform: translateY(-50%);
    height: 16px;
    width: 2px;
    background-color: var(--primaryAltColor);
    opacity: 0;
  }

  &[data-is-active='true'] {
    background-color: var(--menuHoverColor);

    ${DropdownMenuLink} {
      padding-left: 38px;

      &:before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 0;
        height: 1px;
        width: 100%;
        background-color: var(--menuHoverColor);
      }
    }

    &:before {
      opacity: 1;
    }

    &::after {
      border-bottom: 1px solid var(--menuHoverColor);
    }
  }

  ${DropdownMenuColumnThird} & {
    &:hover {
      background-color: var(--menuHoverColor);

      ${DropdownMenuLink} {
        padding-left: 38px;

        &:before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 0;
          height: 1px;
          width: 100%;
          background-color: var(--menuHoverColor);
        }
      }

      &:before {
        opacity: 1;
      }

      &::after {
        border-bottom-color: var(--menuHoverColor);
      }
    }
  }
`;
const DropdownMultiLinkSection = styled.div `
  display: none;
  min-height: calc(100% + 2px);

  &[data-is-active='true'] {
    position: absolute;
    left: 100%;
    top: -1px;
    display: block;
    background-color: var(--backgroundColor);
    border: 1px solid var(--separatorColor);
    border-top: none;
    width: 282px;
    padding-top: 1px;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 1px;
      width: 100%;
      border-top: 1px solid var(--separatorColor);
      z-index: 1;
    }
  }

  &[data-span-cols='3'] {
    ${DropdownMenuColumn} {
      flex: 0 0 calc(33% - ${fluid(18, 24)});
    }
  }

  &[data-span-cols='2'] {
    ${DropdownMenuColumn} {
      flex: 0 0 calc(50% - ${fluid(18, 24)});
    }
  }

  &[data-span-cols='1'] {
    ${DropdownMenuColumn} {
      flex: 0 0 100%;
    }
  }

  > ${DropdownMenuColumn} {
    &:last-of-type {
      ${DropdownMenuItem} {
        &::after {
          content: none;
        }
      }
    }

    ${DropdownMenuItem} {
      &[data-is-active='true'] {
        &::after {
          border-bottom-color: var(--menuHoverColor);
        }
      }

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 16px;
        height: 1px;
        width: calc(100% - 30px);
      }
    }

    ${DropdownMenuColumnThird} {
      ${DropdownMenuItem} {
        &:last-of-type {
          &::after {
            content: none;
          }
        }
      }

      ${DropdownMenuItem} {
        &:hover {
          &::after {
            border-bottom-color: var(--separatorColor);
          }
        }

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 16px;
          height: 1px;
          width: calc(100% - 30px);
          border-bottom-color: var(--separatorColor);
        }
      }
    }
  }
`;
const DropdownMenuCtaWrapper = styled.div `
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  background-color: var(--highlightColorPrimary);
  border-top: 1px solid var(--separatorColor);
  padding: 16px 0;

  > ${Inner} {
    display: flex;
    gap: 12px;
    align-items: center;
  }
`;
const DropdownMenuWrapper = styled.div `
  ${scrollbars};
  ${transition('opacity')};

  position: absolute;
  left: 0;
  transform: translateX(-50%);
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  width: auto;
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColorDark);

  > ${Inner} {
    display: flex;
    gap: ${fluid(18, 24)};
    padding: 0 var(--sitePadding);
  }
`;
const HeaderRevampedStyles = {
    ActionWrapper,
    CloseButton,
    ContactArea,
    Container,
    DesktopNav,
    DesktopNavItem,
    DesktopPrimaryNavLink,
    DropdownMenuColumn,
    DropdownMenuColumnFirst,
    DropdownMenuColumnThird,
    DropdownMenuCtaWrapper,
    DropdownMenuItem,
    DropdownMenuLink,
    DropdownMultiLinkSection,
    DropdownMenuTitle,
    DropdownMenuWrapper,
    ExtraPadding,
    GlobalStyles,
    Grid,
    HeaderContent,
    Inner,
    InputWrapper,
    LinkList,
    LoginBtn,
    Logo,
    LogoReduced,
    LogoWrapper,
    MainContainer,
    MenuButton,
    MobileColumnsWrapper,
    MobileNav,
    MobileNavButton,
    MobileNavInner,
    MobileNavLink,
    Outerlay,
    PhoneLink,
    PhoneLinkMobile,
    PhoneNumber,
    PhoneText,
    SearchButton,
    SearchField,
    SearchForm,
    SearchFormButton,
    SearchInner,
    Spacer,
    TopArea,
    TopLink,
    TopLinks,
};
export default HeaderRevampedStyles;
